// Route-level permission gate. Pair with <AuthProtected> so unauthenticated
// users still get bounced to /login first; this guard handles the
// "you ARE logged in, but you can't access this module" case.
//
// Usage:
//   <AuthProtected>
//     <PermissionGuard moduleId={MODULES.ALARMS} action={PERMISSIONS.VIEW}>
//       <AlarmsPage />
//     </PermissionGuard>
//   </AuthProtected>
//
// Pass `permissionKeys` (an array of module slugs) when a route's
// visibility should OR over several backend modules — e.g. the /sensors
// route is reachable with any of `sensor`, `device`, `access-point`, or
// `v-tag`, mirroring the sidebar's OR-fallback behaviour. `moduleId`
// stays for single-key callers.
//
// `permissionKeys: []` and `permissionKeys: undefined` are equivalent to
// opting out of gating (the guard passes through), matching the
// "common" entry convention in src/modules/index.js.
//
// Pass `fallback` to override where the user is sent on miss — useful when
// you want a 403-style screen instead of a redirect.

import { Navigate } from 'react-router-dom'

import { PERMISSIONS, ROUTES } from '../../constants'
import { useEffectivePermissions } from '../../hooks/useEffectivePermissions'

function PermissionGuard({
  moduleId,
  permissionKeys,
  requiresCompanyType,
  action = PERMISSIONS.VIEW,
  fallback,
  children,
}) {
  const { hasPermission, userType } = useEffectivePermissions()

  // `requiresCompanyType` is an AND-gate layered on top of the
  // permission check: even a role row that grants the module
  // permission can't reach the route when the cached `/me` payload
  // reports a non-COMPANY `type`. Branch Management uses this — the
  // branch CRUD is a tenant-owners task, while the branch *switcher*
  // stays available to every user (separate route, not gated here).
  // `userType === undefined` means /me hasn't loaded yet; treat that
  // as soft-allow so the first render doesn't redirect before the
  // cache is populated.
  const companyGateOK = !requiresCompanyType || userType === undefined || userType === 'COMPANY'
  if (!companyGateOK) {
    return fallback ?? <Navigate to={ROUTES.DASHBOARD} replace />
  }

  // OR-fallback list takes precedence — surfaces sibling permission
  // groupings (e.g. Sensors covers four backend slugs) before falling
  // back to the single `moduleId` lookup.
  if (Array.isArray(permissionKeys)) {
    // Empty array → "common" item, always reachable. Matches the
    // sidebar registry where `permissionKeys: []` means always visible.
    if (permissionKeys.length === 0) return children
    const allowed = permissionKeys.some((k) => hasPermission(k, action))
    if (allowed) return children
    return fallback ?? <Navigate to={ROUTES.DASHBOARD} replace />
  }

  // No moduleId → caller opted out of permission gating. Don't redirect.
  if (moduleId === undefined || moduleId === null || moduleId === '') {
    return children
  }

  if (hasPermission(moduleId, action)) {
    return children
  }

  return fallback ?? <Navigate to={ROUTES.DASHBOARD} replace />
}

export default PermissionGuard
